import { api } from './clients';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  url: string;
  key: string;
}

export class UploadService {
  /**
   * Upload a file to S3 using presigned URLs with tenant isolation
   * @param file - The file to upload
   * @param owner - GitHub repository owner
   * @param repo - GitHub repository name
   * @param onProgress - Optional progress callback
   * @returns Promise resolving to upload result with public URL
   */
  static async uploadFile(
    file: File,
    owner: string,
    repo: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    console.log('[UPLOAD-CLIENT] Starting file upload:', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fileSizeMB: file.size / 1024 / 1024,
      owner,
      repo,
      hasProgressCallback: !!onProgress,
      currentOrigin: window.location.origin,
      currentHostname: window.location.hostname,
    });

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      console.error('[UPLOAD-CLIENT] File validation failed: Unsupported file type', {
        fileName: file.name,
        fileType: file.type,
      });
      throw new Error('Only image and video files are supported');
    }

    // Validate file size
    const maxSize = isImage ? 10 * 1024 * 1024 : 50 * 1024 * 1024; // 10MB for images, 50MB for videos
    if (file.size > maxSize) {
      console.error('[UPLOAD-CLIENT] File validation failed: File too large', {
        fileName: file.name,
        fileSize: file.size,
        fileSizeMB: file.size / 1024 / 1024,
        maxSize,
        maxSizeMB: maxSize / 1024 / 1024,
        isImage,
        isVideo,
      });
      throw new Error(
        `File too large. Maximum size is ${maxSize / 1024 / 1024}MB for ${isImage ? 'images' : 'videos'}`
      );
    }

    console.log('[UPLOAD-CLIENT] File validation passed:', {
      isImage,
      isVideo,
      fileSizeMB: file.size / 1024 / 1024,
      maxSizeMB: maxSize / 1024 / 1024,
    });

    try {
      console.log('[UPLOAD-CLIENT] Step 1: Requesting presigned URL from backend...');

      // Step 1: Get presigned URL from our backend
      const presignedResponse = await api.github['upload']['presigned-url'].$post({
        json: {
          owner,
          repo,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        },
      });

      console.log('[UPLOAD-CLIENT] Presigned URL API response:', {
        ok: presignedResponse.ok,
        status: presignedResponse.status,
        statusText: presignedResponse.statusText,
        headers: Object.fromEntries(presignedResponse.headers.entries()),
      });

      if (!presignedResponse.ok) {
        const errorText = await presignedResponse.text();
        console.error('[UPLOAD-CLIENT] Failed to get presigned URL from backend:', {
          status: presignedResponse.status,
          statusText: presignedResponse.statusText,
          errorText,
          headers: Object.fromEntries(presignedResponse.headers.entries()),
        });
        throw new Error(`Failed to get upload URL: ${errorText}`);
      }

      const { uploadUrl, publicUrl, key } = await presignedResponse.json();

      console.log('[UPLOAD-CLIENT] Presigned URL received successfully:', {
        key,
        publicUrl,
        uploadUrlLength: uploadUrl.length,
        uploadUrlHost: new URL(uploadUrl).host,
        uploadUrlOrigin: new URL(uploadUrl).origin,
      });

      console.log('[UPLOAD-CLIENT] Step 2: Starting direct upload to S3...');

      // Step 2: Upload directly to S3 using presigned URL
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        console.log('[UPLOAD-CLIENT] Creating XHR request:', {
          method: 'PUT',
          url: new URL(uploadUrl).origin + new URL(uploadUrl).pathname,
          contentType: file.type,
          contentLength: file.size,
        });

        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = {
                loaded: event.loaded,
                total: event.total,
                percentage: Math.round((event.loaded / event.total) * 100),
              };
              console.log('[UPLOAD-CLIENT] Upload progress:', progress);
              onProgress(progress);
            }
          });
        }

        xhr.addEventListener('loadstart', () => {
          console.log('[UPLOAD-CLIENT] XHR upload started');
        });

        xhr.addEventListener('load', () => {
          console.log('[UPLOAD-CLIENT] XHR load event:', {
            status: xhr.status,
            statusText: xhr.statusText,
            readyState: xhr.readyState,
            responseHeaders: xhr.getAllResponseHeaders(),
            responseText: xhr.responseText?.substring(0, 500), // First 500 chars
          });

          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('[UPLOAD-CLIENT] Upload completed successfully');
            resolve();
          } else {
            console.error('[UPLOAD-CLIENT] Upload failed with HTTP error:', {
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText,
            });
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', (event) => {
          console.error('[UPLOAD-CLIENT] XHR error event:', {
            type: event.type,
            loaded: event.loaded,
            total: event.total,
            status: xhr.status,
            statusText: xhr.statusText,
            readyState: xhr.readyState,
            responseText: xhr.responseText,
          });
          reject(new Error('Upload failed due to network error'));
        });

        xhr.addEventListener('abort', () => {
          console.log('[UPLOAD-CLIENT] XHR aborted');
          reject(new Error('Upload was aborted'));
        });

        xhr.addEventListener('timeout', () => {
          console.error('[UPLOAD-CLIENT] XHR timeout');
          reject(new Error('Upload timed out'));
        });

        console.log('[UPLOAD-CLIENT] Opening XHR connection...');
        xhr.open('PUT', uploadUrl);

        console.log('[UPLOAD-CLIENT] Setting request headers...');
        xhr.setRequestHeader('Content-Type', file.type);

        console.log('[UPLOAD-CLIENT] Sending file data...');
        xhr.send(file);
      });

      const result = {
        url: publicUrl,
        key,
      };

      console.log('[UPLOAD-CLIENT] Upload completed successfully, verifying file exists in S3:', result);

      // Verify the file actually exists in S3
      try {
        console.log('[UPLOAD-CLIENT] Step 3: Verifying file exists in S3...');

        const verifyResponse = await api.github['upload']['verify'].$post({
          json: { key },
        });

        if (!verifyResponse.ok) {
          const errorText = await verifyResponse.text();
          console.error('[UPLOAD-CLIENT] File verification API failed:', {
            status: verifyResponse.status,
            errorText,
            key,
          });
          throw new Error(`File verification failed: ${errorText}`);
        }

        const verificationResult = await verifyResponse.json();
        console.log('[UPLOAD-CLIENT] File verification result:', {
          key,
          exists: verificationResult.exists,
          verificationResult,
        });

        if (!verificationResult.exists) {
          console.error('[UPLOAD-CLIENT] CRITICAL: File upload appeared successful but file does not exist in S3!', {
            key,
            publicUrl,
            fileName: file.name,
            fileSize: file.size,
            verificationResult,
          });
          throw new Error('Upload completed but file verification failed - file not found in S3');
        }

        console.log('[UPLOAD-CLIENT] File upload and verification both successful');
      } catch (verificationError: any) {
        console.error('[UPLOAD-CLIENT] File verification step failed:', {
          key,
          fileName: file.name,
          error:
            verificationError instanceof Error
              ? {
                  name: verificationError.name,
                  message: verificationError.message,
                  stack: verificationError.stack,
                }
              : verificationError,
        });

        // Don't throw here - let the upload appear successful but log the verification failure
        // This way we can see if the upload works but verification fails vs upload actually failing
        console.warn('[UPLOAD-CLIENT] Continuing despite verification failure - upload may have succeeded');
      }

      return result;
    } catch (error) {
      console.error('[UPLOAD-CLIENT] Upload failed:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        owner,
        repo,
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : error,
      });

      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Upload failed due to unknown error');
    }
  }

  /**
   * Handle drag and drop or paste events
   * @param event - The drop or paste event
   * @param owner - GitHub repository owner
   * @param repo - GitHub repository name
   * @param onProgress - Optional progress callback
   * @returns Promise resolving to array of upload results
   */
  static async handleFileEvent(
    event: DragEvent | ClipboardEvent,
    owner: string,
    repo: string,
    onProgress?: (fileName: string, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    console.log('[UPLOAD-CLIENT] Handling file event:', {
      eventType: event.type,
      owner,
      repo,
      hasProgressCallback: !!onProgress,
    });

    let files: FileList | null = null;

    if ('dataTransfer' in event && event.dataTransfer) {
      files = event.dataTransfer.files;
      console.log('[UPLOAD-CLIENT] Files from dataTransfer:', {
        count: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });
    } else if ('clipboardData' in event && event.clipboardData) {
      files = event.clipboardData.files;
      console.log('[UPLOAD-CLIENT] Files from clipboardData:', {
        count: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });
    }

    if (!files || files.length === 0) {
      console.log('[UPLOAD-CLIENT] No files found in event');
      return [];
    }

    const results: UploadResult[] = [];
    const uploadPromises: Promise<UploadResult>[] = [];

    console.log('[UPLOAD-CLIENT] Starting parallel uploads for', files.length, 'files');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log('[UPLOAD-CLIENT] Queuing upload for file:', {
        index: i,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });

      const promise = this.uploadFile(
        file,
        owner,
        repo,
        onProgress ? (progress) => onProgress(file.name, progress) : undefined
      );

      uploadPromises.push(promise);
    }

    // Wait for all uploads to complete
    try {
      console.log('[UPLOAD-CLIENT] Waiting for all uploads to complete...');
      const uploadResults = await Promise.all(uploadPromises);
      results.push(...uploadResults);
      console.log('[UPLOAD-CLIENT] All uploads completed successfully:', {
        totalFiles: files.length,
        successfulUploads: uploadResults.length,
        results: uploadResults.map((r) => ({ url: r.url, key: r.key })),
      });
    } catch (error) {
      console.error('[UPLOAD-CLIENT] Some uploads failed:', {
        totalFiles: files.length,
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : error,
      });
      throw error;
    }

    return results;
  }

  /**
   * Utility to check if a file is supported for upload
   * @param file - The file to check
   * @returns boolean indicating if file is supported
   */
  static isFileSupported(file: File): boolean {
    return file.type.startsWith('image/') || file.type.startsWith('video/');
  }

  /**
   * Format file size for display
   * @param bytes - Size in bytes
   * @returns Formatted string
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Delete a file from S3
   * @param url - The public URL of the file to delete
   * @returns Promise that resolves when file is deleted
   */
  static async deleteFile(url: string): Promise<void> {
    try {
      const response = await api.github['upload']['file'].$delete({
        json: { url },
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to delete file: ${error}`);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('File deletion failed due to unknown error');
    }
  }
}
